import { Pipe, PipeTransform } from '@angular/core';
import { TranslocoService } from '@ngneat/transloco';

export type HourCycle = 'h11' | 'h12' | 'h23' | 'h24';

export interface FolioCardTimeOptions {
  /** Reference "now" for relative formatting (useful in tests). Defaults to current time. */
  now?: Date;
  /** If true, include time for dates in the current year (default: true). */
  includeTimeForThisYear?: boolean;
  /** Preferred hour cycle for time formatting. */
  hourCycle?: HourCycle;
  /** If true, use the compact "ago" format (1m ago, 2h ago, 3d ago, 1w ago). */
  compactAgo?: boolean;
}

@Pipe({
  name: 'folioCardTimeAgo',
  pure: true,
})
export class FolioCardTimeAgoPipe implements PipeTransform {
  constructor(private transloco: TranslocoService) {}

  transform(dateInput: Date | number | string, opts: FolioCardTimeOptions = {}): string {
    const { includeTimeForThisYear = true, hourCycle, now = new Date(), compactAgo = false } = opts;

    const locale = this.transloco.getActiveLang?.();
    const date = this.toDate(dateInput);
    if (!date) return '';

    if (compactAgo) {
      return this.formatCompactAgo(date, now as Date, locale);
    }

    const diffMs = (now as Date).getTime() - date.getTime();
    const diffSec = Math.round(diffMs / 1000);
    const diffMin = Math.round(diffSec / 60);
    const diffHour = Math.round(diffSec / 3600);

    // < 60s → "Just now"
    if (Math.abs(diffSec) < 60) {
      return this.transloco.translate('translations.time.justNow');
    }

    // < 60m → "1 m" | "12 m"
    if (Math.abs(diffMin) < 60) {
      const m = Math.abs(diffMin);
      return m === 1
        ? this.transloco.translate('translations.time.minute.one')
        : this.transloco.translate('translations.time.minute.other', { count: m });
    }

    // same day (by local calendar) and <24h → "1 h" | "3 h"
    if (Math.abs(diffHour) < 24 && this.isSameDay(date, now)) {
      const h = Math.abs(diffHour);
      return h === 1
        ? this.transloco.translate('translations.time.hour.one')
        : this.transloco.translate('translations.time.hour.other', { count: h });
    }

    // Yesterday → "Yesterday at 14:35"
    if (this.isYesterday(date, now)) {
      return this.transloco.translate('translations.time.yesterdayAt', { time: this.formatTime(date, locale, hourCycle) });
    }

    // Same year → "5 Sep at 14:35" or "5 Sep"
    if (date.getFullYear() === (now as Date).getFullYear()) {
      const dayMonth = this.formatDayMonth(date, locale);
      return includeTimeForThisYear
        ? this.transloco.translate('translations.time.dayMonthAt', { dayMonth, time: this.formatTime(date, locale, hourCycle) })
        : dayMonth;
    }

    // Other years → "5 Sep 2023"
    return this.formatDayMonthYear(date, locale);
  }

  // ---------- Helpers ----------
  private formatCompactAgo(date: Date, now: Date, locale: string): string {
    const diffSec = Math.round((now.getTime() - date.getTime()) / 1000);

    if (Math.abs(diffSec) < 60) {
      return this.transloco.translate('translations.time.justNow');
    }

    const diffMin = Math.round(diffSec / 60);
    if (Math.abs(diffMin) < 60) {
      return this.transloco.translate('translations.time.ago.minute', { count: Math.abs(diffMin) });
    }

    const diffHour = Math.round(diffSec / 3600);
    if (Math.abs(diffHour) < 24) {
      return this.transloco.translate('translations.time.ago.hour', { count: Math.abs(diffHour) });
    }

    const diffDay = Math.round(diffSec / 86400);
    if (Math.abs(diffDay) < 7) {
      return this.transloco.translate('translations.time.ago.day', { count: Math.abs(diffDay) });
    }

    const diffWeek = Math.round(diffDay / 7);
    if (Math.abs(diffWeek) < 5) {
      return this.transloco.translate('translations.time.ago.week', { count: Math.abs(diffWeek) });
    }

    return date.getFullYear() === now.getFullYear() ? this.formatDayMonth(date, locale) : this.formatDayMonthYear(date, locale);
  }

  private toDate(input: Date | number | string): Date | null {
    if (input instanceof Date) return input;
    // Backend timestamps are naive UTC (no Z/offset suffix). Without normalizing, `new Date(...)`
    // parses them as local time, which skews relative times by the local UTC offset.
    let normalized = input;
    if (typeof normalized === 'string' && normalized.includes('T') && !/[Zz]|[+-]\d{2}:?\d{2}$/.test(normalized)) {
      normalized = `${normalized}Z`;
    }
    const d = new Date(normalized);
    return isNaN(d.getTime()) ? null : d;
  }

  private isSameDay(a: Date, b: Date): boolean {
    return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
  }

  private isYesterday(target: Date, ref: Date): boolean {
    const start = new Date(ref);
    start.setHours(0, 0, 0, 0);
    start.setDate(start.getDate() - 1);
    const end = new Date(start);
    end.setHours(23, 59, 59, 999);
    return target >= start && target <= end;
  }

  private formatTime(d: Date, locale: string, hourCycle?: HourCycle): string {
    return new Intl.DateTimeFormat(locale, {
      hour: '2-digit',
      minute: '2-digit',
      hourCycle,
    }).format(d);
  }

  private formatDayMonth(d: Date, locale: string): string {
    return new Intl.DateTimeFormat(locale, {
      day: 'numeric',
      month: 'short',
    }).format(d);
  }

  private formatDayMonthYear(d: Date, locale: string): string {
    return new Intl.DateTimeFormat(locale, {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    }).format(d);
  }
}
